/**
 * Attachment list + add buttons for travel items and events.
 * Supports picking from the Fias file system or uploading from the user's computer.
 */

import { useState, useRef } from 'react';
import { Paperclip, Upload, Loader2, X, FileText, Image, Film, Music, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { FileBrowser } from '@/components/files/FileBrowser';
import { toast } from '@/components/ui/use-toast';
import { captureError } from '@/lib/telemetry';
import type { UserFileResponse } from '@/lib/api';
import api from '@/lib/api';
import type { TravelAttachmentResponse } from '../api';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document'))
    return FileText;
  return File;
}

interface TravelAttachmentsProps {
  attachments: TravelAttachmentResponse[];
  onAddAttachment: (fileId: string) => Promise<unknown>;
  onRemoveAttachment: (attachmentId: string) => Promise<unknown>;
}

/**
 * Upload a file to S3 via presigned URL, confirm it, then return the UserFileResponse.
 */
async function uploadAndConfirm(file: globalThis.File): Promise<UserFileResponse> {
  // 1. Get presigned upload URL
  const presigned = await api.getUploadUrl({
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    category: 'general',
  });

  // 2. PUT the file to S3
  const putResponse = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error(`Upload failed (${putResponse.status})`);
  }

  // 3. Confirm upload with the API
  const { file: confirmed } = await api.confirmUpload(presigned.fileId, {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    s3Key: presigned.s3Key,
    category: 'general',
    tags: ['travel'],
  });

  return confirmed;
}

export function TravelAttachments({
  attachments,
  onAddAttachment,
  onRemoveAttachment,
}: TravelAttachmentsProps) {
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UserFileResponse | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLocalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const confirmed = await uploadAndConfirm(file);
        await onAddAttachment(confirmed.fileId);
      }
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelAttachments',
        action: 'upload',
      });
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFiasFileSelect = (file: UserFileResponse) => {
    setSelectedFile(file);
  };

  const handleFiasConfirm = async () => {
    if (!selectedFile) return;
    try {
      await onAddAttachment(selectedFile.fileId);
      setSelectedFile(null);
      setShowFilePicker(false);
    } catch (err) {
      captureError(err instanceof Error ? err : new Error(String(err)), {
        component: 'TravelAttachments',
        action: 'attach',
      });
      toast({
        title: 'Failed to attach file',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (attachment: TravelAttachmentResponse) => {
    const { downloadUrl } = await api.getDownloadUrl(attachment.fileId);
    window.open(downloadUrl, '_blank');
  };

  return (
    <div className="space-y-1.5">
      {/* Existing attachments */}
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((att) => {
            const Icon = getFileIcon(att.mimeType);
            return (
              <div
                key={att.attachmentId}
                className="group flex items-center gap-2 rounded border border-border px-2 py-1.5 text-sm hover:bg-accent/50 transition-colors"
              >
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <button
                  className="truncate text-left hover:underline flex-1 min-w-0"
                  onClick={() => handleDownload(att)}
                >
                  {att.fileName || att.fileId}
                </button>
                {att.fileSize != null && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatFileSize(att.fileSize)}
                  </span>
                )}
                <button
                  onClick={() => onRemoveAttachment(att.attachmentId)}
                  className="p-0.5 rounded hover:bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add attachment buttons */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={() => setShowFilePicker(true)}
        >
          <Paperclip className="h-3 w-3 mr-1" /> Fias Files
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading...
            </>
          ) : (
            <>
              <Upload className="h-3 w-3 mr-1" /> Upload
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleLocalUpload}
        />
      </div>

      {/* Fias file picker dialog */}
      <Dialog open={showFilePicker} onOpenChange={setShowFilePicker}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Attach a File
            </DialogTitle>
            <DialogDescription>Choose a file from your Fias storage to attach.</DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
            <FileBrowser
              onFileSelect={handleFiasFileSelect}
              multiSelect={false}
              status="active"
              className="h-full"
            />
          </div>

          {selectedFile && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate font-medium">{selectedFile.fileName}</span>
                <span className="text-sm text-muted-foreground">
                  ({formatFileSize(selectedFile.sizeBytes)})
                </span>
              </div>
              <Button onClick={handleFiasConfirm}>Attach</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
